import React, { useState, useContext } from "react";
import "../Resume-Upload/ResumeUpload.css";
import Table from "../../Common/Table/Table";
import Button from "../../Common/Button/Button";
import uploadIcon from "../../Assets/Images/resume-upload-icon.png";
import viewCandidateIcon from "../../Assets/Images/candidate-view.png";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import FileIcon from "../../Common/Common-Function/IconMapping.js";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar.js";
import SidePopup from "../../Common/SidePopup/SidePopup.js";
import { DynamicContext } from "../../Context/DynamicContext.js";
import { get, post } from "../../helper/Api_helper.js";
import {
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  UPDATE_CANDIDATE_DETAILS,
  VENDORS_SEARCH,
} from "../../helper/url_helper.js";
import CandidateDetailsContainer from "../../Common/Candidate-Details-Container/CandidateDetailsContainer.js";
import DotLoader from "../../Common/Pre-Loader/DotLoader.jsx";

function SpreadsheetResults(props) {
  const { isDarkMode } = useContext(DynamicContext);
  const {
    spreadsheetResults,
    setUploadedResumes,
    showHeader,
    UploadedResumes,
    setSpreadsheetResults,
    failedSpreadsheets = [],
    setFailedSpreadsheets,
    resetSpreadsheetState,
  } = props || {};
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);

  const groupedResults = React.useMemo(() => {
    const grouped = {};
    const validResults = spreadsheetResults.filter(
      (result) => result.candidate_id,
    );
    validResults.forEach((result) => {
      if (!grouped[result.name]) {
        grouped[result.name] = {
          candidates: [],
          statusCounts: { created: 0, updated: 0, incomplete: 0 },
        };
      }
      const exists = grouped[result.name].candidates.some(
        (item) => item.candidate_id === result.candidate_id,
      );
      if (!exists) {
        grouped[result.name].candidates.push(result);
        const status = (result.candidate_creation_status || "").toLowerCase();
        if (status === "created") {
          grouped[result.name].statusCounts.created++;
        } else if (status === "updated") {
          grouped[result.name].statusCounts.updated++;
        } else if (status === "incomplete") {
          grouped[result.name].statusCounts.incomplete++;
        }
      }
    });
    return grouped;
  }, [spreadsheetResults]);

  const totalStatusCounts = React.useMemo(() => {
    let totalCreated = 0;
    let totalUpdated = 0;
    let totalIncomplete = 0;

    Object.values(groupedResults).forEach((fileData) => {
      totalCreated += fileData.statusCounts.created;
      totalUpdated += fileData.statusCounts.updated;
      totalIncomplete += fileData.statusCounts.incomplete;
    });
    failedSpreadsheets.forEach((file) => {
      if (file.isIncomplete) {
        totalIncomplete++;
      }
    });

    return { totalCreated, totalUpdated, totalIncomplete };
  }, [groupedResults, failedSpreadsheets]);

  const tableData = React.useMemo(() => {
    const data = [];
    Object.entries(groupedResults).forEach(([filename, fileData]) => {
      fileData.candidates.forEach((candidate, index) => {
        data.push({
          id: `${filename}-${candidate.candidate_id || index}`,
          filename,
          candidateData: candidate,
          candidate_id: candidate.candidate_id,
          candidate_name: candidate.candidate_name || "N/A",
          candidate_creation_status: candidate.candidate_creation_status,
          created_at: candidate.created_at,
        });
      });
    });
    return data;
  }, [groupedResults]);

  const sortedUploadedResumes = React.useMemo(() => {
    return [...UploadedResumes].sort((a, b) => {
      const isAFailed = failedSpreadsheets.some((f) => f.name === a.name);
      const isBFailed = failedSpreadsheets.some((f) => f.name === b.name);

      if (isAFailed && !isBFailed) return 1;
      if (!isAFailed && isBFailed) return -1;
      return 0;
    });
  }, [UploadedResumes, failedSpreadsheets]);

  const fetchCandidateById = async (candidateId) => {
    try {
      const res = await get(
        VYLABS_BASE_URL,
        `${GET_CANDIDATE_BY_ID}?id=${candidateId}`,
      );
      const candidate = res?.candidate;
      const candidateFiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidateFiles && candidateFiles.length > 0
          ? candidateFiles[0].presigned_url
          : null;

      const updatedCandidate = {
        ...candidate,
        secondaryEmail: candidate.secondary_email,
        secondaryPhone: candidate.secondary_phone,
        vendorID: candidate.vendor_id,
        lineAddress1: candidate.line_address_1,
        lineAddress2: candidate.line_address_2,
        preferredSalary: candidate.preferred_salary,
        zipCode: candidate.zip_code,
        parsedResumeData,
        files: candidateFiles || null,
        presignedUrl,
        isFileBased: false,
        avatar: res?.profile_img_url,
      };

      if (updatedCandidate.vendor_id) {
        try {
          const vendorResponse = await get(
            VYLABS_BASE_URL,
            `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendor_id}`,
          );
          setAssignedVendor(vendorResponse);
          updatedCandidate.vendorDetails = vendorResponse || null;
        } catch (err) {
          console.error("Error fetching vendor details:", err);
          updatedCandidate.vendorDetails = null;
        }
      } else {
        updatedCandidate.vendorDetails = null;
      }

      setSelectedCandidate(updatedCandidate);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching candidate:", error);
      Toast("error", "Failed to load candidate details");
    }
  };

  const handleEditCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);

      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedCandidate?.years_of_experience,
    );

    const parsedExp = getValidExperience(
      selectedCandidate?.parsedResumeData?.BasicInfo?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }
    const candidateWithFormattedSkills = {
      ...selectedCandidate,
      name: selectedCandidate.name || selectedCandidate.BasicInfo?.Name || "",
      email:
        selectedCandidate.email || selectedCandidate.BasicInfo?.Email || "",
      secondaryEmail:
        selectedCandidate.secondaryEmail ||
        selectedCandidate.BasicInfo?.secondaryEmail ||
        "",
      phone:
        selectedCandidate.phone ||
        selectedCandidate.BasicInfo?.PhoneNumber ||
        "",
      secondaryPhone:
        selectedCandidate.secondaryPhone ||
        selectedCandidate.BasicInfo?.secondaryPhone ||
        "",
      city:
        selectedCandidate.city ||
        selectedCandidate.BasicInfo?.CurrentLocationCity ||
        "",
      state:
        selectedCandidate.state ||
        selectedCandidate.BasicInfo?.CurrentLocationState ||
        "",
      country:
        selectedCandidate.country ||
        selectedCandidate.BasicInfo?.CurrentLocationCountry ||
        "",
      skills: Array.isArray(selectedCandidate.skills)
        ? selectedCandidate.skills
        : selectedCandidate.parsedResumeData?.Skills || [],
      vendorID:
        selectedCandidate.vendorID ||
        selectedCandidate.BasicInfo?.vendorID ||
        "",
      designation:
        selectedCandidate.designation?.trim() ||
        selectedCandidate.parsedResumeData?.CareerHistory?.JobDetails?.[0]
          ?.JobTitle ||
        "",
      experience: experienceValue,
      lineAddress1:
        selectedCandidate.lineAddress1 ||
        selectedCandidate.BasicInfo?.LineAddress1 ||
        "",
      lineAddress2:
        selectedCandidate.lineAddress2 ||
        selectedCandidate.BasicInfo?.LineAddress2 ||
        "",
      zipCode:
        selectedCandidate.zipCode || selectedCandidate.BasicInfo?.ZipCode || "",
      preferredSalary:
        selectedCandidate.preferredSalary ||
        selectedCandidate.BasicInfo?.PreferredSalary ||
        "",
    };

    setOriginalCandidateData({ ...selectedCandidate });
    setEditedCandidateData(candidateWithFormattedSkills);
    setIsEditingCandidate(true);
    setEmailError("");
    setEmailTouched(false);
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setSelectedCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
    setEmailError("");
    setEmailTouched(false);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => {
      const updated = {
        ...prev,
        ...data,
      };
      return updated;
    });
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    const email =
      editedCandidateData.email !== undefined
        ? editedCandidateData.email
        : selectedCandidate.email || "";

    if (!email.trim()) {
      setEmailError("Email is required");
      setEmailTouched(true);
      Toast("error", "Email is required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Invalid email format");
      setEmailTouched(true);
      Toast("error", "Please fill all required fields correctly");
      return;
    }
    setEmailError("");
    setEmailTouched(false);
    try {
      const candidateData = { id: selectedCandidate.id };

      Object.keys(editedCandidateData).forEach((key) => {
        const newValue = editedCandidateData[key];
        const oldValue = selectedCandidate[key];

        const isChanged = Array.isArray(newValue)
          ? JSON.stringify(newValue) !== JSON.stringify(oldValue)
          : newValue !== oldValue;

        if (
          isChanged &&
          newValue !== undefined &&
          newValue !== null &&
          newValue !== ""
        ) {
          candidateData[key] = newValue;
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("error", "No changes to save");
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        const updatedCandidate = {
          ...selectedCandidate,
          ...candidateData,
        };

        if (
          candidateData.vendorID !== undefined &&
          candidateData.vendorID !== selectedCandidate.vendorID
        ) {
          if (candidateData.vendorID) {
            try {
              const vendorResponse = await get(
                VYLABS_BASE_URL,
                `${GET_VENDOR_DETAILS_BY_ID}?id=${candidateData.vendorID}`,
              );
              updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
              setAssignedVendor(vendorResponse);
            } catch (err) {
              console.error("Error fetching vendor details:", err);
              updatedCandidate.vendorDetails = null;
              setAssignedVendor(null);
            }
          } else {
            updatedCandidate.vendorDetails = null;
          }
        }

        setSelectedCandidate(updatedCandidate);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        Toast("success", "Candidate updated successfully");

        if (setSpreadsheetResults) {
          setSpreadsheetResults((prev) =>
            prev.map((item) => {
              if (item.candidate_id === updatedCandidate.id) {
                return {
                  ...item,
                  candidate_name: updatedCandidate.name,
                };
              }
              return item;
            }),
          );
        }
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Failed to update candidate";
      Toast("error", backendMessage);
    }
  };

  const fetchVendors = async (inputValue = "") => {
    try {
      setVendorOptionLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(inputValue)}`,
      );

      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors
            .filter((v) => v?.id && v?.name)
            .map((v) => ({
              label: v.contact_person
                ? `${v.name} - ${v.contact_person}`
                : v.name,
              value: v.id,
            }))
        : [];

      setVendorList(fetchedVendors);
    } catch (err) {
      console.error("fetchVendors error", err);
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) {
      fetchVendors(value);
    } else {
      setVendorList([]);
    }
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    resetCandidateEdit();
    setActiveTab(0);
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => <span>{index + 1}</span>,
      width: "80px",
    },
    {
      name: "Candidate Name",
      selector: (row) => (
        <CustomTooltip title={row.candidate_name} arrow>
          <span>{row.candidate_name}</span>
        </CustomTooltip>
      ),
      width: "250px",
    },
    {
      name: "Spreadsheet File",
      selector: (row) => (
        <CustomTooltip title={row.filename} arrow>
          <span className="parsing-file-name-container">
            <FileIcon fileName={row.filename} />
            {row.filename}
          </span>
        </CustomTooltip>
      ),
      width: "250px",
    },
    {
      name: "Status",
      selector: (row) => {
        const status = (row.candidate_creation_status || "N/A").toLowerCase();
        return (
          <span
            style={{
              color:
                status === "created"
                  ? "green"
                  : status === "updated"
                    ? "#ffc100"
                    : status === "incomplete"
                      ? "#FF6B6B"
                      : "gray",
              fontSize: "14px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => {
        if (!row.candidate_id) return null;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
            }}
            className="action-icons"
          >
            <CustomTooltip title="View Candidate" arrow>
              <img
                src={viewCandidateIcon}
                alt="View Candidate"
                className="parser-table-action-icons"
                onClick={() => {
                  fetchCandidateById(row.candidate_id, "viewCandidate");
                }}
                style={{ cursor: "pointer" }}
              />
            </CustomTooltip>
          </div>
        );
      },
      width: "100px",
    },
  ];

  const handleClearResults = () => {
    if (window.socketRef?.current) {
      window.socketRef.current.close(1000, "User returned to upload page");
      window.socketRef.current = null;
    }
    if (window.countdownIntervalRef?.current) {
      clearInterval(window.countdownIntervalRef.current);
      window.countdownIntervalRef.current = null;
    }
    setSpreadsheetResults([]);
    setUploadedResumes([]);
    setFailedSpreadsheets([]);
    if (resetSpreadsheetState) {
      resetSpreadsheetState();
    }
  };

  return (
    <>
      <div className="header-and-resume-button-container">
        <div className="upload-parser-resume-header">
          {showHeader && (
            <div className="header-parser-main-container">
              <div className="header-icon-title-container">
                <h2 className="upload-resume-header">
                  Spreadsheet Import Results
                </h2>
              </div>
              <div className="right-side-button-container">
                <Button
                  className="import-action-buttons"
                  label="Back to Upload"
                  iconSrc={uploadIcon}
                  event={handleClearResults}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="spreadsheet-summary-container"
        style={{
          background: isDarkMode ? "rgba(30, 30, 40, 0.7)" : "white",
          borderRadius: "12px",
          padding: "20px",
          margin: "20px",
          border: isDarkMode
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <h3
              style={{
                color: isDarkMode ? "#fff" : "#6860EE",
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Import Summary
            </h3>
            <p
              style={{
                color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                fontSize: "14px",
                margin: "4px 0 0 0",
              }}
            >
              {UploadedResumes.length} files processed
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div
              style={{
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(104, 96, 238, 0.15)"
                  : "rgba(104, 96, 238, 0.08)",
                borderRadius: "10px",
                border: isDarkMode
                  ? "1px solid rgba(104, 96, 238, 0.3)"
                  : "1px solid rgba(104, 96, 238, 0.2)",
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666",
                  marginBottom: "4px",
                }}
              >
                Total Files
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: isDarkMode ? "#fff" : "#6860EE",
                  lineHeight: "1",
                }}
              >
                {UploadedResumes.length}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666",
                  marginTop: "4px",
                }}
              >
                Uploaded
              </div>
            </div>
            <div
              style={{
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(76, 175, 80, 0.15)"
                  : "rgba(76, 175, 80, 0.08)",
                borderRadius: "10px",
                border: isDarkMode
                  ? "1px solid rgba(76, 175, 80, 0.3)"
                  : "1px solid rgba(76, 175, 80, 0.2)",
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666",
                  marginBottom: "4px",
                }}
              >
                Created
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: isDarkMode ? "#4CAF50" : "#2E7D32",
                  lineHeight: "1",
                }}
              >
                {totalStatusCounts.totalCreated}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: isDarkMode ? "rgba(76, 175, 80, 0.8)" : "#4CAF50",
                  marginTop: "4px",
                }}
              >
                New candidates
              </div>
            </div>
            <div
              style={{
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(255, 152, 0, 0.15)"
                  : "rgba(255, 152, 0, 0.08)",
                borderRadius: "10px",
                border: isDarkMode
                  ? "1px solid rgba(255, 152, 0, 0.3)"
                  : "1px solid rgba(255, 152, 0, 0.2)",
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666",
                  marginBottom: "4px",
                }}
              >
                Updated
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: isDarkMode ? "#FF9800" : "#EF6C00",
                  lineHeight: "1",
                }}
              >
                {totalStatusCounts.totalUpdated}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: isDarkMode ? "rgba(255, 152, 0, 0.8)" : "#FF9800",
                  marginTop: "4px",
                }}
              >
                Existing candidates
              </div>
            </div>
            <div
              style={{
                padding: "12px 16px",
                background: isDarkMode
                  ? "rgba(255, 107, 107, 0.15)"
                  : "rgba(255, 107, 107, 0.08)",
                borderRadius: "10px",
                border: isDarkMode
                  ? "1px solid rgba(255, 107, 107, 0.3)"
                  : "1px solid rgba(255, 107, 107, 0.2)",
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666",
                  marginBottom: "4px",
                }}
              >
                Incomplete
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: isDarkMode ? "#FF6B6B" : "#D32F2F",
                  lineHeight: "1",
                }}
              >
                {totalStatusCounts.totalIncomplete}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: isDarkMode ? "rgba(255, 107, 107, 0.8)" : "#FF6B6B",
                  marginTop: "4px",
                }}
              >
                Partial processing
              </div>
            </div>
          </div>
        </div>
        <div style={{ maxHeight: "130px", overflowY: "scroll" }}>
          {sortedUploadedResumes.map((file) => {
            const filename = file.name;
            const fileData = groupedResults[filename];
            const failedFile = failedSpreadsheets.find(
              (f) => f.name === filename,
            );

            if (fileData) {
              const {
                created,
                updated,
                incomplete: baseIncomplete,
              } = fileData.statusCounts;
              const failedFileForThis = failedSpreadsheets.find(
                (f) => f.name === filename,
              );
              const incomplete =
                baseIncomplete + (failedFileForThis?.isIncomplete ? 1 : 0);
              return (
                <div
                  key={filename}
                  style={{
                    padding: "12px",
                    marginBottom: "8px",
                    background: isDarkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FileIcon fileName={filename} />
                    <strong
                      style={{
                        color: isDarkMode ? "#fff" : "#333",
                        fontSize: "14px",
                      }}
                    >
                      {filename}
                    </strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {created > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          background: isDarkMode
                            ? "rgba(76, 175, 80, 0.15)"
                            : "rgba(76, 175, 80, 0.1)",
                          borderRadius: "6px",
                          border: isDarkMode
                            ? "1px solid rgba(76, 175, 80, 0.3)"
                            : "1px solid rgba(76, 175, 80, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#4CAF50",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#4CAF50" : "#2E7D32",
                          }}
                        >
                          Created: {created}
                        </span>
                      </div>
                    )}
                    {updated > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          background: isDarkMode
                            ? "rgba(255, 152, 0, 0.15)"
                            : "rgba(255, 152, 0, 0.1)",
                          borderRadius: "6px",
                          border: isDarkMode
                            ? "1px solid rgba(255, 152, 0, 0.3)"
                            : "1px solid rgba(255, 152, 0, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#FF9800",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#FF9800" : "#EF6C00",
                          }}
                        >
                          Updated: {updated}
                        </span>
                      </div>
                    )}
                    {incomplete > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          background: isDarkMode
                            ? "rgba(255, 107, 107, 0.15)"
                            : "rgba(255, 107, 107, 0.1)",
                          borderRadius: "6px",
                          border: isDarkMode
                            ? "1px solid rgba(255, 107, 107, 0.3)"
                            : "1px solid rgba(255, 107, 107, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: "#FF6B6B",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: isDarkMode ? "#FF6B6B" : "#D32F2F",
                          }}
                        >
                          Incomplete: {incomplete}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            } else if (failedFile) {
              return (
                <div
                  key={failedFile.id || failedFile.name}
                  style={{
                    padding: "12px",
                    marginBottom: "8px",
                    background: isDarkMode
                      ? `rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.05)`
                      : `rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.03)`,
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FileIcon fileName={failedFile.name} />
                    <div>
                      <strong
                        style={{
                          color: isDarkMode ? "#fff" : "#333",
                          fontSize: "14px",
                        }}
                      >
                        {failedFile.name}
                      </strong>
                      <div
                        style={{
                          fontSize: "12px",
                          color: isDarkMode
                            ? failedFile.isIncomplete
                              ? "rgba(255, 152, 0, 0.9)"
                              : "rgba(244, 67, 54, 0.9)"
                            : failedFile.isIncomplete
                              ? "#EF6C00"
                              : "#D32F2F",
                          marginTop: "2px",
                          fontWeight: "500",
                        }}
                      >
                        <strong>Error:</strong> {failedFile.error}
                      </div>
                      {failedFile.isIncomplete && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: isDarkMode
                              ? "rgba(255, 152, 0, 0.9)"
                              : "#EF6C00",
                            marginTop: "2px",
                            fontWeight: "500",
                          }}
                        >
                          ⚠️ Partial data was extracted but processing was
                          incomplete
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "4px 8px",
                      background: isDarkMode
                        ? `rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.15)`
                        : `rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.1)`,
                      borderRadius: "6px",
                      border: isDarkMode
                        ? `1px solid rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.3)`
                        : `1px solid rgba(${failedFile.isIncomplete ? "255, 152, 0" : "244, 67, 54"}, 0.2)`,
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: failedFile.isIncomplete
                          ? "#FF9800"
                          : "#F44336",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        color: isDarkMode
                          ? failedFile.isIncomplete
                            ? "#FF9800"
                            : "#F44336"
                          : failedFile.isIncomplete
                            ? "#EF6C00"
                            : "#D32F2F",
                      }}
                    >
                      {failedFile.isIncomplete ? "Incomplete" : "Failed"}
                    </span>
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  key={filename}
                  style={{
                    padding: "12px",
                    marginBottom: "8px",
                    background: isDarkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FileIcon fileName={filename} />
                    <strong
                      style={{
                        color: isDarkMode ? "#fff" : "#333",
                        fontSize: "14px",
                      }}
                    >
                      {filename}
                    </strong>
                  </div>
                  <div className="parsed-status-container">
                    <DotLoader size={1} borderWidth={5} duration={1.5} />
                    <span>Processing</span>
                  </div>
                </div>
              );
            }
          })}
        </div>
      </div>

      <div className="main-resume-container">
        <div className="table-container">
          <Table
            columns={columns}
            data={tableData}
            pagination
            highlightOnHover
            fixedHeader
            fixedHeaderScrollHeight="500px"
            dataNotFoundMessage="No spreadsheet import results yet..!"
            clearSelectedRows={false}
            backgroundColor="var(--body__background)"
            color="var(--header__color)"
          />
        </div>
      </div>

      <SidePopup
        key={selectedCandidate?.id || "no-candidate"}
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Candidate Details"
        showDeleteButton={false}
      >
        <CandidateDetailsContainer
          selectedCandidate={selectedCandidate}
          isEditingCandidate={isEditingCandidate}
          editedCandidateData={editedCandidateData}
          setEditedCandidateData={setEditedCandidateData}
          handleEditCandidate={handleEditCandidate}
          resetCandidateEdit={resetCandidateEdit}
          saveCandidateChanges={saveCandidateChanges}
          emailError={emailError}
          emailTouched={emailTouched}
          setEmailError={setEmailError}
          setEmailTouched={setEmailTouched}
          handleCandidateChange={handleCandidateChange}
          candidate={selectedCandidate}
          activeTab={activeTab}
          handleTabChange={setActiveTab}
          assignedVendor={assignedVendor}
          hasSearched={true}
          vendorList={vendorList}
          vendorOptionLoading={vendorOptionLoading}
          handleVendorInputChange={handleVendorInputChange}
        />
      </SidePopup>
    </>
  );
}

export default SpreadsheetResults;
